// The 19 "detail" tokens (P4A-170 item 25b architecture refactor, plus items
// 4/13's nav-active-bg/border + faq-accordion-body-bg). All string | null —
// Strapi's raw null passes through untouched (toThemeTokens() no longer
// JS-defaults these), and the CSS fallback chain at each consuming selector
// (or the shared :root defaults, theme.css) supplies the effective value:
// a primitive alias for the SAFE tokens, a literal default for the rest.
type DetailTokenKey =
  | 'quote-accent'
  | 'quote-surface'
  | 'quote-text'
  | 'connector'
  | 'card-text'
  | 'cta-accent-bg'
  | 'cta-accent-text'
  | 'donation-amount-accent'
  | 'calculator-screen-bg'
  | 'calculator-screen-text'
  | 'calculator-screen-plus'
  | 'calculator-ticker-label'
  | 'faq-accordion-bg'
  | 'faq-accordion-text'
  | 'input-surface-bg'
  | 'input-surface-text'
  | 'nav-active-bg'
  | 'nav-active-border'
  | 'faq-accordion-body-bg'
  | 'calculator-border-color';

export type ThemeTokens = {
    colors: {
      primary: string;
      secondary: string;
      extend1: string;
      extend2: string;
      accent1: string;
      accent2: string;
      background: string;
      text: string;
      heading: string;
      // Primitives/text/heading above and the two sitewide tokens below
      // (secondary-color, link-color) are unrelated to the detail-token
      // null-passthrough mechanism — required strings, always JS-defaulted.
      'secondary-color': string;
      'link-color': string;
    } & Record<DetailTokenKey, string | null>;
    fonts: {
      heading: string;
      body: string;
      headingUrl?: string;
      bodyUrl?: string;
    };
    logoUrl?: string | null;
    wordmarkUrl?: string | null;
    // Resolved theme key, trimmed + lowercased by CampaignThemeProvider so
    // consumers can compare against a plain lowercase literal. null when no
    // theme is configured.
    activeThemeKey: string | null;
    // Whether the resolved theme is a dark palette. Admin-declared on the theme
    // itself so contrast-driven rendering doesn't have to match key strings.
    isDark: boolean;
  };

export const defaultTokens: ThemeTokens = {
    colors: {
      primary: '#363A94',
      secondary: '#E64923',
      extend1: '#04081E',
      extend2: '#F6F700',
      accent1: '#42FF57',
      accent2: '#151F49',
      background: '#FFFFFF',
      text: '#212529',
      heading: '#363A94',
      'quote-accent': '#E64923',
      'quote-surface': '#FFFFFF',
      'quote-text': '#363A94',
      connector: '#4b49c6',
      'card-text': '#212529',
      'cta-accent-bg': '#363A94',
      'cta-accent-text': '#FFFFFF',
      'donation-amount-accent': '#E64923',
      'calculator-screen-bg': 'transparent',
      'calculator-screen-text': '#363A94',
      'calculator-screen-plus': '#F15A2A',
      'calculator-ticker-label': '#000',
      // Matches theme.css's :root alias (var(--bs-background)) so a cleared
      // Strapi field and the JS default resolve to the same surface.
      'faq-accordion-bg': '#FFFFFF',
      'faq-accordion-text': '#212529',
      // Deliberately identical in light and dark (2027) campaigns — these
      // light-pill input surfaces stay light/readable in both themes.
      'input-surface-bg': '#fff',
      'input-surface-text': '#212529',
      'nav-active-bg': '#6AD1E2',
      'nav-active-border': '#F49F38',
      'faq-accordion-body-bg': '#EBF7FC',
      'calculator-border-color': 'transparent',
      // Bootstrap's own default --bs-secondary-color light value ("gray-300" muted text).
      'secondary-color': 'rgba(33, 37, 41, 0.75)',
      // Bootstrap's own default --bs-link-color light value — sitewide link contrast.
      'link-color': '#0d6efd'
    },
    fonts: {
      heading: 'Bogart, serif',
      body: 'Poppins, sans-serif',
      bodyUrl:
        'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap',
    },
    activeThemeKey: null,
    isDark: false,
  };
